import { ChangeDetectionStrategy, Component, Inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
    AbstractNotification,
    CommandService,
    ModalController,
    NotificationType,
    NotifierController,
    PopupWindow,
    StateService,
    Tracker,
} from 'flux-core';
import { Subscription, timer } from 'rxjs';
import { filter, finalize, last, map, take } from 'rxjs/operators';
import { TeamPortalWindow } from '../team-portal-window/team-portal-window.cmp';
import { UserCommandEvent } from '../../user/command/user-command-event';
import { UserPlanStatusChange } from '../../user/command/update-user-plan-status.cmd';
import { UserLocator } from '../../user/user-locator.svc';
import { SubscriptionStatusTypes } from '../../permission/model/user-subscription.mdl';
import { AccountReactivationStorage } from './account-reactivation-storage';

/**
 * Supported reactivation modal flows.
 */
export enum AccountReactivationType {
    Paused = 'paused',
    PaymentFailed = 'payment-failed',
}

/**
 * Shows a reactivation modal for paused subscriptions.
 */
@Component({
    selector: 'account-reactivation-window',
    templateUrl: './account-reactivation-window.cmp.html',
    styleUrls: [ './account-reactivation-window.scss' ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountReactivationWindow extends PopupWindow implements OnInit, OnDestroy {
    protected static readonly RESUME_RELOAD_DELAY_MS = 15000;

    /**
     * Notification ids for account resume feedback.
     */
    protected static readonly RESUME_SUCCESS_NOTIFICATION_ID = 'resume-account-success';
    protected static readonly RESUME_ERROR_NOTIFICATION_ID = 'resume-account-error';
    protected static readonly LOAD_TRACK_EVENT = 'dialog.accountPaused.load';
    protected static readonly CLOSE_TRACK_EVENT = 'dialog.accountPaused.close';
    protected static readonly PAUSED_CLICK_TRACK_EVENT = 'dialog.accountPaused.resume.click';
    protected static readonly PAYMENT_FAILED_CLICK_TRACK_EVENT = 'dialog.accountPaused.updatePaymentMethod.click';

    /**
     * The reactivation flow to show in the modal.
     */
    @Input() public type: AccountReactivationType = AccountReactivationType.Paused;


    /**
     * The modal overlay element.
     */
    @ViewChild( 'container', { static: true })
    protected container;

    /**
     * The modal window element.
     */
    @ViewChild( 'window', { static: true })
    protected window;

    /**
     * Active subscriptions.
     */
    protected subs: Array<Subscription> = [];

    /**
     * Tracks the native resume mutation state.
     */
    public isSubmitting = false;

    constructor(
        @Inject( StateService ) protected state: StateService<any, any>,
        protected modalController: ModalController,
        protected translate: TranslateService,
        protected commandService: CommandService,
        protected notifierController: NotifierController,
        protected userLocator: UserLocator,
    ) {
        super();
    }

    /**
     * Whether the modal is shown for payment failures.
     */
    public get isPaymentFailed(): boolean {
        return this.type === AccountReactivationType.PaymentFailed;
    }

    /**
     * Main modal heading.
     */
    public get heading(): string {
        return this.translate.instant( 'NOTIFICATIONS.PAUSED_USER.HEADER' );
    }

    /**
     * Supporting message shown inside the modal.
     */
    public get description(): string {
        if ( this.isPaymentFailed ) {
            return this.translate.instant( 'PAYMENT_FAILED_MESSAGES.MODAL_DESCRIPTION' );
        }

        return this.translate.instant( 'NOTIFICATIONS.PAUSED_USER.MODAL_DESCRIPTION' );
    }

    /**
     * Primary action label.
     */
    public get actionLabel(): string {
        return this.isPaymentFailed
            ? this.translate.instant( 'PAYMENT_FAILED_MESSAGES.BUTTONS' )
            : this.translate.instant( 'NOTIFICATIONS.PAUSED_USER.BUTTON' );
    }

    /**
     * Starts the modal show animation.
     */
    public ngOnInit(): void {
        Tracker.track( AccountReactivationWindow.LOAD_TRACK_EVENT, {
            value1Type: 'modalType',
            value1: this.type,
        });
        this.subs.push( this.showWindow( this.container, this.window ).subscribe());
    }

    /**
     * Hides the modal.
     */
    public closeWindow(): void {
        const sub = this.hideWindow( this.container, this.window ).subscribe({
            complete: () => this.modalController.hide(),
        });
        this.subs.push( sub );

        Tracker.track( AccountReactivationWindow.CLOSE_TRACK_EVENT, {
            value1Type: 'modalType',
            value1: this.type,
        });
    }

    /**
     * Open the primary reactivation action.
     */
    public openPrimaryAction(): void {
        if ( !this.isPaymentFailed && this.isSubmitting ) {
            return;
        }

        Tracker.track( this.primaryActionTrackEvent, {
            value1Type: 'modalType',
            value1: this.type,
        });

        if ( this.isPaymentFailed ) {
            this.markPaymentRecoveryPending();
            this.modalController.show( TeamPortalWindow, {
                inputs: {
                    page: 'billing',
                },
            });
            return;
        }

        this.isSubmitting = true;
        const sub = this.commandService.dispatch( UserCommandEvent.updateUserPlanStatus, {
            planStatus: UserPlanStatusChange.Resume,
        }).pipe(
            last(),
            map( results => {
                if ( results && results.resultData && results.resultData[0]) {
                    const resultObject = JSON.parse( results.resultData[0]);
                    return !!resultObject?.data?.userUpdate;
                }
                return false;
            }),
            finalize( () => this.isSubmitting = false ),
        ).subscribe({
            next: isSuccess => {
                if ( !isSuccess ) {
                    this.handleShowNotification(
                        AccountReactivationWindow.RESUME_ERROR_NOTIFICATION_ID,
                        this.translate.instant( 'NOTIFICATIONS.PAUSED_USER.RESUME_ERROR' ),
                        NotificationType.Error,
                    );
                    return;
                }

                this.handleShowNotification(
                    AccountReactivationWindow.RESUME_SUCCESS_NOTIFICATION_ID,
                    this.translate.instant( 'NOTIFICATIONS.PAUSED_USER.RESUME_SUCCESS' ),
                    NotificationType.Success,
                );
                this.markResumeReloadPending();
                this.waitForResumedSubscription();
            },
            error: () => {
                this.handleShowNotification(
                    AccountReactivationWindow.RESUME_ERROR_NOTIFICATION_ID,
                    this.translate.instant( 'NOTIFICATIONS.PAUSED_USER.RESUME_ERROR' ),
                    NotificationType.Error,
                );
            },
        });
        this.subs.push( sub );
    }

    /**
     * Tracking event name for the primary CTA.
     */
    protected get primaryActionTrackEvent(): string {
        return this.isPaymentFailed
            ? AccountReactivationWindow.PAYMENT_FAILED_CLICK_TRACK_EVENT
            : AccountReactivationWindow.PAUSED_CLICK_TRACK_EVENT;
    }

    /**
     * Shows a notification for the resume flow.
     */
    protected handleShowNotification( id: string, heading: string, type: NotificationType ): void {
        this.notifierController.show( id, AbstractNotification, type,
            { inputs: { heading, autoDismiss: true }},
        );
    }

    /**
     * Wait until the local user subscription model reflects the resumed state
     * before reloading, with a fallback timeout for slower environments.
     */
    protected waitForResumedSubscription(): void {
        const reloadDelaySub = timer( AccountReactivationWindow.RESUME_RELOAD_DELAY_MS ).pipe(
            take( 1 ),
        ).subscribe(() => this.navigateAfterResume());

        let resumedSub: Subscription;
        const fallbackSub = timer( 5000 ).pipe(
            take( 1 ),
        ).subscribe(() => {
            resumedSub?.unsubscribe();
        });

        resumedSub = this.userLocator.getUserDataAfterUserSubscriptionStarted().pipe(
            filter( user => !!user?.userSubscription ),
            filter( user => user.userSubscription.subStatus !== SubscriptionStatusTypes.PAUSED ),
            take( 1 ),
        ).subscribe(() => {
            fallbackSub.unsubscribe();
        });

        this.subs.push( reloadDelaySub, fallbackSub, resumedSub );
    }

    /**
     * Stores a short-lived flag so the next app boot can suppress stale paused-state UI.
     */
    protected markResumeReloadPending(): void {
        const currentUser = this.state.get( 'CurrentUser' );
        if ( !currentUser ) {
            return;
        }

        sessionStorage.setItem(
            AccountReactivationStorage.getResumeReloadStorageKey( currentUser ),
            Date.now().toString(),
        );
    }

    /**
     * Stores a short-lived flag so the next app boot can suppress stale payment recovery UI.
     */
    protected markPaymentRecoveryPending(): void {
        const currentUser = this.state.get( 'CurrentUser' );
        if ( !currentUser ) {
            return;
        }

        sessionStorage.setItem(
            AccountReactivationStorage.getPaymentRecoveryStorageKey( currentUser ),
            Date.now().toString(),
        );
    }

    /**
     * Reload the app after the account is resumed.
     */
    /* istanbul ignore next */
    protected navigateAfterResume(): void {
        window.location.reload();
    }

    /**
     * Cleanup subscriptions.
     */
    public ngOnDestroy(): void {
        while ( this.subs.length > 0 ) {
            this.subs.pop().unsubscribe();
        }
    }
}
